/**
 * Hard ceiling for AuthView cold-boot — users must never see an unbounded spinner.
 * Bundle marker: boot_deadline_15s_v1 (grep deployed JS to verify).
 */
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  BOOT_DEADLINE_MARKER,
  BOOT_DEADLINE_MS,
  isBootSurrendered,
  shouldApplyBootDeadline,
} from './bootDeadline';
import { hasReachedUsableAuthedState } from './authTerminalLatch';

export const AUTH_INIT_CEILING_MS = BOOT_DEADLINE_MS;
/** @deprecated use BOOT_DEADLINE_MARKER — kept for grep continuity */
export const AUTH_INIT_TIMEOUT_MARKER = 'auth_init_timeout_ceiling_v1';

export type AuthInitForceTimeout = () => void;

let pendingStep = 'boot';
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let forceTimeoutHandler: AuthInitForceTimeout | null = null;
/** Absolute boot anchor — must not reset during restore retry flicker. */
let bootStartedAt: number | null = null;
let timeoutFired = false;
/** Watchdog is inert until startAuthInitWatchdog() — never arm on bare module import. */
let watchdogEnabled = false;

export function getAuthInitPendingStep(): string {
  return pendingStep;
}

export function setAuthInitStep(step: string): void {
  pendingStep = step;
}

export function registerAuthInitForceTimeout(handler: AuthInitForceTimeout): void {
  forceTimeoutHandler = handler;
}

function fireAuthInitTimeout(): void {
  if (timeoutFired) return;
  if (isBootSurrendered()) {
    if (watchdogTimer) clearTimeout(watchdogTimer);
    watchdogTimer = null;
    return;
  }
  if (!shouldApplyBootDeadline()) {
    if (watchdogTimer) clearTimeout(watchdogTimer);
    const now = Date.now();
    if (bootStartedAt) {
      const elapsed = now - bootStartedAt;
      const remaining = Math.max(BOOT_DEADLINE_MS - elapsed, 500);
      watchdogTimer = setTimeout(fireAuthInitTimeout, remaining);
    }
    return;
  }
  timeoutFired = true;
  watchdogTimer = null;
  try {
    const step = pendingStep || 'unknown';
    console.warn(`[js:auth] boot_deadline pending_step=${step} marker=${BOOT_DEADLINE_MARKER}`);
    diagnosticLog('warn', 'other', 'auth_init_timeout', {
      pending_step: step,
      marker: BOOT_DEADLINE_MARKER,
      ceiling_ms: BOOT_DEADLINE_MS,
    });
  } catch { /* diagnostic only */ }
  forceTimeoutHandler?.();
}

function syncAuthInitWatchdog(isLoading: boolean): void {
  if (!isLoading) {
    if (hasReachedUsableAuthedState() || timeoutFired) {
      bootStartedAt = null;
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
    }
    return;
  }

  const now = Date.now();
  if (!bootStartedAt) bootStartedAt = now;
  const elapsed = now - bootStartedAt;
  if (elapsed >= BOOT_DEADLINE_MS) {
    fireAuthInitTimeout();
    return;
  }

  if (watchdogTimer) clearTimeout(watchdogTimer);
  watchdogTimer = setTimeout(fireAuthInitTimeout, BOOT_DEADLINE_MS - elapsed);
}

/** Begin bootstrap ceiling tracking — call from AppLayout (or login shell) on mount, not at import. */
export function startAuthInitWatchdog(isLoading = true): void {
  watchdogEnabled = true;
  bootStartedAt = null;
  timeoutFired = false;
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
  syncAuthInitWatchdog(isLoading);
}

/** Tear down watchdog timers — test teardown and route unmount. */
export function stopAuthInitWatchdog(): void {
  watchdogEnabled = false;
  bootStartedAt = null;
  timeoutFired = false;
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
}

export function armAuthInitWatchdog(isLoading: boolean): void {
  if (!watchdogEnabled) return;
  syncAuthInitWatchdog(isLoading);
}

export function resetAuthInitWatchdogForTests(): void {
  stopAuthInitWatchdog();
  pendingStep = 'boot';
  forceTimeoutHandler = null;
}

/** Join api.ts single-flight refresh — never a second POST /api/auth/refresh (#602). */
export async function refreshAuthJsonSession(
  refreshToken: string,
  _timeoutMs = BOOT_DEADLINE_MS,
): Promise<Response> {
  try {
    const stored = localStorage.getItem('maestro_auth');
    const current: Record<string, unknown> = stored ? JSON.parse(stored) : {};
    if (!current.refreshToken && refreshToken) {
      localStorage.setItem('maestro_auth', JSON.stringify({ ...current, refreshToken }));
    }
  } catch { /* seed is best-effort */ }

  const { refreshAccessTokenShared } = await import('../services/api');
  const result = await refreshAccessTokenShared('authJsonRestore:refreshAuthJsonSession');
  if (result.accessToken) {
    return new Response(JSON.stringify({
      access_token: result.accessToken,
      refresh_token: result.refreshToken || refreshToken,
      session: {
        access_token: result.accessToken,
        refresh_token: result.refreshToken || refreshToken,
      },
    }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  if (result.fatalRejected) {
    return new Response(JSON.stringify({
      error: 'Refresh token is not valid',
      error_code: 'invalid_refresh_token',
      error_description: 'Refresh token is not valid',
    }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  return new Response(JSON.stringify({
    error: 'refresh_unavailable',
    error_code: result.alreadyUsedAbsorbed ? 'already_used' : 'transient',
    transient: true,
  }), {
    status: result.alreadyUsedAbsorbed ? 401 : 503,
    headers: { 'Content-Type': 'application/json' },
  });
}
